import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import { OrderUI, OrderStatus } from '@/types/order';
import { Customer } from '@/types/customer';
import { OrderStatusBadge, OrderSourceBadge, OrderPrintButton } from '@/components/orders';
import { ErrorWarningIcon } from '@/components/order-line/ErrorWarningIcon';

interface OrderDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderUI | null;
  customer: Customer | null;
  showPrintButton?: boolean;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
  }).format(amount);
};

const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ isOpen, onClose, order, customer, showPrintButton = false }) => {
  const { t } = useTranslation();
  
  if (!order) return null;

  const { code, status, orderSource, notes, orderLines, totalPrice, createdAt } = order;
  
  console.log('OrderDetailModal - Full order object:', order);
  console.log('OrderDetailModal - orderLines structure:', orderLines);

  // Calculate completion statistics (completed + delivery)
  console.log('OrderDetailModal - orderLines:', orderLines);
  console.log('OrderDetailModal - orderLines length:', orderLines.length);
  
  const completedLines = orderLines.filter(line => {
    const status = line.currentStatus?.status;
    console.log('OrderDetailModal - line status:', line.id, status);
    return status === 'completed' || status === 'delivery';
  }).length;
  
  const totalLines = orderLines.length;
  
  console.log('OrderDetailModal - completedLines:', completedLines);
  console.log('OrderDetailModal - totalLines:', totalLines);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('tooltips.orders.orderDetailWithCode', { code })}
      size="2xl"
      footer={
        <div className="flex justify-between items-center w-full">
          <div>
            {showPrintButton && order && (
              <OrderPrintButton order={order} />
            )}
          </div>
          <Button variant="outline" onClick={onClose}>Đóng</Button>
        </div>
      }
    >
      <div className="space-y-6 p-2">
        {/* General Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 bg-gray-50 p-4 rounded-lg">
          <div>
            <p className="text-sm text-gray-500">{t('orders.fields.customer')}</p>
            <p className="font-medium text-gray-900">{customer?.name || 'N/A'}</p>
            <p className="text-sm text-gray-600">{customer?.phone || ''}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('orders.fields.status')}</p>
            <OrderStatusBadge status={status as OrderStatus} />
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('orders.completionProgress.label')}</p>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                completedLines === totalLines 
                  ? 'bg-green-100 text-green-800' 
                  : completedLines > 0 
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
              }`}>
                {t('orders.completionProgress.completedLines', { completed: completedLines, total: totalLines })}
              </span>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500">Nguồn đơn</p>
            <OrderSourceBadge source={orderSource} />
          </div>
          <div>
            <p className="text-sm text-gray-500">Ngày tạo</p>
            <p className="font-medium text-gray-900">{formatDate(createdAt)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Ngày giao hàng</p>
            <p className="font-medium text-gray-900">{formatDate(order.deliveryAt)}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-sm text-gray-500">Ghi chú</p>
            <p className="font-medium text-gray-900 italic">{notes || 'Không có ghi chú'}</p>
          </div>
        </div>

        {/* Order Lines */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Danh sách dịch vụ</h3>
          <div className="overflow-hidden border border-gray-200 rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mã DV
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tên Dịch vụ
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Số lượng
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Đơn giá
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Giảm giá (%)
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Thành tiền
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ghi chú
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orderLines.map((line) => (
                    <tr key={line.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <span>{line.serviceCode || line.serviceId}</span>
                          <ErrorWarningIcon orderLine={line} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {line.serviceName || 'Unknown Service'}
                          </span>
                          {line.priceSource && line.priceSource !== 'service_base' && (
                            <span className="text-xs text-gray-500 mt-1">
                              {line.priceSource === 'customer_specific' && t('orders.modals.create.priceSource.customerSpecific')}
                              {line.priceSource === 'customer_group' && t('orders.modals.create.priceSource.customerGroup')}
                              {line.priceSource === 'staff' && t('orders.modals.create.priceSource.staff')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-center">
                        {line.quantity}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right">
                        {formatCurrency(line.sellingPrice)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-center">
                        {line.discountPercentage}%
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                        {formatCurrency(line.totalPrice)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {line.notes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="flex justify-end pt-4 border-t">
            <div className="text-right">
                <p className="text-sm text-gray-500">Tổng cộng</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPrice)}</p>
            </div>
        </div>

      </div>
    </Modal>
  );
};

export default OrderDetailModal;
